import { Card } from '@magnetic/card'
import { Flex } from '@magnetic/flex'
import { Heading } from '@magnetic/heading'
import { Notification } from '@magnetic/notification'
import { Text } from '@magnetic/text'
import { KeyValue } from '@magnetic/key-value'
import { Button } from '@magnetic/button'
import {
  InProgress,
  Positive,
  Negative,
  WarningStatus,
  Pause,
  Trash,
  ArrowsClockwise,
} from '@/lib/icons'
import {
  deriveSyncHealthFromState,
  getHealthIcon,
  getSyncIcon,
  getImageDeploymentIcon,
  getImageDeploymentText,
  getApplicationState,
} from '@/utils/applicationStatus'
import type { Application as ProtoApplication } from '@/lib/generated-types'

type Application = Pick<ProtoApplication, 'state' | 'stateMessage' | 'imageDeploymentStatus' | 'deletedAt'>

interface ApplicationStateCardProps {
  application: Application
  onForceDelete: () => void
  onReconcileCRD?: () => void
  forceDeletePending?: boolean
  reconcileCRDPending?: boolean
}

export function ApplicationStateCard({
  application,
  onForceDelete,
  onReconcileCRD,
  forceDeletePending = false,
  reconcileCRDPending = false,
}: ApplicationStateCardProps) {
  const { healthStatus, syncStatus } = deriveSyncHealthFromState(application.state)
  const { isSuspended, isDeleting, isOutOfSync, isDegraded, isHealthy } = getApplicationState(application)
  const stateLabel = application.state || 'Unknown'
  const stateIcon = getStateIcon(application.state)

  return (
    <Card>
      <Flex direction="vertical" gap="md" style={{ padding: 16 }}>
        <Heading size="sub-section">Application State</Heading>

        <KeyValue>
          <KeyValue.Pair
            pair={[
              'State',
              <Flex align="center" gap={8}>
                {stateIcon}
                <Text size="p3" weight="semi-bold">{stateLabel}</Text>
              </Flex>,
            ]}
          />

          <KeyValue.Pair
            pair={[
              'Health Status',
              <Flex align="center" gap={8}>
                {getHealthIcon(healthStatus)}
                <Text size="p3" weight="semi-bold">
                  {healthStatus}
                </Text>
              </Flex>,
            ]}
          />

          <KeyValue.Pair
            pair={[
              'Sync Status',
              <Flex align="center" gap={8}>
                {getSyncIcon(syncStatus)}
                <Text size="p3" weight="semi-bold">
                  {syncStatus}
                </Text>
              </Flex>,
            ]}
          />

          <KeyValue.Pair
            pair={[
              'Image Deployment',
              <Flex align="center" gap={8}>
                {getImageDeploymentIcon(application.imageDeploymentStatus)}
                <Text size="p3" weight="semi-bold">{getImageDeploymentText(application.imageDeploymentStatus)}</Text>
              </Flex>,
            ]}
          />

          {isDeleting && (
            <KeyValue.Pair
              pair={[
                'Lifecycle',
                <Flex align="center" gap={8}>
                  <InProgress />
                  <Text size="p3" weight="semi-bold" color="warning">Deleting...</Text>
                </Flex>,
              ]}
            />
          )}
        </KeyValue>

        {/* State-based Action Guidance - Only show one banner based on priority */}
        {isHealthy && !isDeleting && (
          <Notification status="positive" title="Application is operational">
            <Text size="p3">The application is healthy and synchronized.</Text>
          </Notification>
        )}

        {!isHealthy && isDegraded && !isSuspended && !isDeleting && (
          <Notification status="negative" title="Degraded Health">
            <Flex direction="vertical" gap="xs">
              <Text size="p4">
                Some resources are unhealthy. This usually indicates pods are crashing, failing health checks, or stuck in error states.
              </Text>
              <Text size="p4" weight="semi-bold">What to do:</Text>
              <Text size="p4">- Check Logs for pod crashes and errors</Text>
              <Text size="p4">- Review Resources for failing components</Text>
              <Text size="p4">- Sync after fixing repository configuration errors</Text>
              <Text size="p4">- If issues persist, delete and recreate the application</Text>
            </Flex>
          </Notification>
        )}

        {!isHealthy && !isDegraded && isOutOfSync && !isSuspended && !isDeleting && (
          <Notification status="warning" title="Out of Sync">
            <Flex direction="vertical" gap="xs">
              <Text size="p4">
                The deployed state differs from Git. The application is running, but repository changes have not been deployed yet.
              </Text>
              <Text size="p4" weight="semi-bold">What to do:</Text>
              <Text size="p4">- Click Sync to deploy latest Git changes</Text>
              <Text size="p4">- Check Diff to preview what will change</Text>
              <Text size="p4">- Enable Auto-Sync to deploy future changes automatically</Text>
            </Flex>
          </Notification>
        )}

        {isSuspended && (
          <Notification status="information" title="Application Suspended">
            <Text size="p4">Automated sync is disabled and replicas are set to 0. Click Resume to restore.</Text>
          </Notification>
        )}

        {isDeleting && (
          <Notification status="warning" title="Deletion in Progress">
            <Flex direction="vertical" gap="sm">
              <Text size="p4">
                The application is being deleted. If this is stuck for more than 5 minutes, use force delete.
              </Text>
              <Button
                destructive
                size="sm"
                onClick={onForceDelete}
                disabled={forceDeletePending}
                icon={<Trash size={14} />}
              >
                Force Delete
              </Button>
            </Flex>
          </Notification>
        )}

        {!isDeleting && onReconcileCRD && (
          <Flex justify="flex-end">
            <Button
              kind="secondary"
              size="sm"
              onClick={onReconcileCRD}
              disabled={reconcileCRDPending}
              icon={<ArrowsClockwise size={14} />}
            >
              {reconcileCRDPending ? 'Reconciling...' : 'Reconcile ArgoCD CRD'}
            </Button>
          </Flex>
        )}
      </Flex>
    </Card>
  )
}

function getStateIcon(state?: string) {
  switch (state) {
    case 'Healthy':
      return <Positive />
    case 'Degraded':
    case 'HelmValidationFailed':
    case 'CRDCreationFailed':
      return <Negative />
    case 'OutOfSync':
      return <WarningStatus />
    case 'Suspended':
      return <Pause size={20} />
    case 'ValidatingHelm':
    case 'CreatingCRD':
    case 'Syncing':
      return <InProgress />
    default:
      return <InProgress />
  }
}
